import type { MouseEventHandler, ReactNode } from "react";
import {
  createDesktopExternalOpener,
  getActiveHostAuth,
  isDesktopParentOrigin,
} from "../lib/host-auth";

type Props = {
  href: string;
  children: ReactNode;
  id?: string;
};

/**
 * Hands `href` to the desktop sidecar's shell instead of a plain navigation
 * when embedded there. There we are a cross-origin iframe in a Tauri webview,
 * where `target="_blank"` opens nothing at all, so a plain anchor reads as
 * broken to the member. Every other host gets `undefined` back, so the
 * anchor's ordinary behaviour (and `onClick`, if the caller passed one) is
 * untouched. Shared by `ExternalLink` and any other anchor that needs the
 * same host bridge without using that component's styling.
 */
export function externalLinkClickHandler(
  href: string,
  onClick?: () => void,
): MouseEventHandler<HTMLAnchorElement> | undefined {
  const hostAuth = getActiveHostAuth();
  const openInHost =
    hostAuth && isDesktopParentOrigin(hostAuth.parentOrigin)
      ? createDesktopExternalOpener(hostAuth)
      : null;
  if (!openInHost && !onClick) return undefined;
  return (event) => {
    if (openInHost) {
      event.preventDefault();
      openInHost(href);
    }
    onClick?.();
  };
}

/**
 * An anchor that still works inside the desktop sidecar (see
 * `externalLinkClickHandler`).
 *
 * The `href` stays on the element either way, so the link is still a link for
 * screen readers, middle-click, and copy-link.
 */
export function ExternalLink({ href, children, id }: Props) {
  return (
    <a
      id={id}
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={externalLinkClickHandler(href)}
    >
      {children}
    </a>
  );
}
